import React, { ReactElement, useCallback, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import { useSnackbar } from 'notistack';
import cn from 'classnames';
import { useHistory } from 'react-router';
import Routes from 'routes';
import { isLastElement } from 'globals/utils';
import defaultLocales from 'globals/locales/default';
import FsStatusLabel from 'ui-elements/common-elements/FsStatusLabel';
import FsDate from 'ui-elements/common-elements/FsDate';

import { ReportStatus } from 'globals/types';
import { getColor } from './FsReportsTable.style';
import {
  Cell,
  Email,
  Name,
  Reference,
  HeadCell,
  HeadLabel,
  Row,
} from './FsReportsTable.style-elements';

export interface FsReportsTableSingleRowProps {
  id: string | number;
  name: string | ReactElement;
  email: string;
  code: string;
  statusType: ReportStatus;
  date: number;
}

export interface FsReportsTableProps {
  rows?: FsReportsTableSingleRowProps[];
  isLongVersion?: boolean;
  headLabels?: string[];
}

const FsReportsTable: React.FC<FsReportsTableProps> = ({
  rows = [],
  isLongVersion,
  headLabels = [],
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [activeIdx, setActiveIdx] = useState<number>();

  const redirect = useCallback(
    (idx: number): void => {
      history.push({ pathname: `${Routes.REPORT}/id/${idx}` });
    },
    [history]
  );

  const handleReadyClick = (idx: number): void => {
    setActiveIdx(idx);
    redirect(idx);
  };

  const handleUnreadyClick = (): void => {
    enqueueSnackbar(defaultLocales.openReportNotificationError, {
      variant: 'info',
    });
  };

  // const handleIconClick = (e: React.MouseEvent): void => {
  //   e.preventDefault();
  //   e.stopPropagation();
  // };

  return (
    <TableContainer>
      <Table>
        {headLabels.length ? (
          <TableHead>
            <TableRow data-test="table-head-row">
              {headLabels.map((label) => (
                <HeadCell key={label}>
                  <HeadLabel>{label}</HeadLabel>
                </HeadCell>
              ))}
            </TableRow>
          </TableHead>
        ) : null}
        <TableBody>
          {rows.map(({ id, name, statusType, code, date, email }, idx) => (
            <Row
              key={id}
              data-test="table-row"
              hover={statusType === ReportStatus.READY}
              selected={statusType === ReportStatus.READY && activeIdx === idx}
              onClick={(): void =>
                statusType === ReportStatus.READY
                  ? handleReadyClick(idx)
                  : handleUnreadyClick()
              }
              className={cn({ isLast: isLastElement(idx, rows?.length) })}
            >
              <Cell component="th">
                <Name>{name}</Name>
                <Email>{email}</Email>
              </Cell>
              <Cell width={isLongVersion ? '15%' : undefined}>
                <Reference>{code}</Reference>
              </Cell>
              <Cell width={isLongVersion ? '20%' : undefined}>
                <FsDate withTime date={date} withYear />
              </Cell>
              <Cell width={isLongVersion ? '20%' : undefined}>
                <FsStatusLabel bgColor={getColor(statusType)}>
                  {defaultLocales.enums[statusType]}
                </FsStatusLabel>
              </Cell>
              {/* {isLongVersion && ( */}
              {/*  <Cell withIconOnly> */}
              {/*    {statusType === ReportStatuses.READY && ( */}
              {/*      <FsIconButton size="small" onClick={handleIconClick}> */}
              {/*        <FsMessageIcon */}
              {/*          color={idx === 5 ? purple.V1 : undefined} */}
              {/*        /> */}
              {/*      </FsIconButton> */}
              {/*    )} */}
              {/*  </Cell> */}
              {/* )} */}
            </Row>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default FsReportsTable;
